import deepmerge from 'deepmerge';
import Maybe from 'true-myth/maybe';
import type { z } from 'zod';

import type { Id } from '../data-contract/id';
import {
    customizationResponseSchema,
    customizationUiConfigurationResponseSchema,
    defaultTheme,
    oidcConfigurationSchema,
    tenantAssetsDefault,
    tenantAssetsResponseSchema,
    tenantConfigDefault,
    tenantConfigSchema,
    tenantThemeSchema,
} from './customization-response';

const uiConfigurationSchema = customizationUiConfigurationResponseSchema.extend({
    assets: tenantAssetsResponseSchema
        .partial()
        .optional()
        .default(tenantAssetsResponseSchema.parse(tenantAssetsDefault))
        .transform(
            ({
                companyLogo,
                dashboardHeroImageUrl,
                favicon,
                onboardingImageUrl,
                profileBackgroundImageUrl,
                ...rest
            }) => ({
                ...rest,
                companyLogoUrl: new URL(companyLogo ?? tenantAssetsDefault.companyLogo),
                dashboardHeroImageUrl: new URL(dashboardHeroImageUrl ?? tenantAssetsDefault.dashboardHeroImageUrl),
                faviconUrl: new URL(favicon ?? tenantAssetsDefault.favicon),
                onboardingImageUrl: new URL(onboardingImageUrl ?? tenantAssetsDefault.onboardingImageUrl),
                profileBackgroundImageUrl: new URL(
                    profileBackgroundImageUrl ?? tenantAssetsDefault.profileBackgroundImageUrl,
                ),
            }),
        ),
    config: tenantConfigSchema
        .partial()
        .optional()
        .default(tenantConfigDefault)
        .transform((config) => tenantConfigSchema.parse(config)),
    theme: tenantThemeSchema
        .partial()
        .optional()
        .default(tenantThemeSchema.parse(defaultTheme))
        .transform((theme) => tenantThemeSchema.passthrough().parse(deepmerge(defaultTheme, theme))),
});

export const customizationSchema = customizationResponseSchema
    .extend({
        oidcConfiguration: oidcConfigurationSchema,
        uiConfiguration: uiConfigurationSchema,
    })
    .transform(({ translationOverrides, ...rest }) => ({
        ...rest,
        translationOverrides: Maybe.of(translationOverrides),
    }));

export type Customization = z.infer<typeof customizationSchema>;

export type CustomizationProvider = {
    readonly reset: () => void;
    get customization(): Maybe<Customization>;
    get isDefaultCustomization(): boolean;
    get assumedTenantId(): Maybe<Id>;
};

export type CustomizationProviderOptions = {
    readonly location: Location;
};
